import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, FolderKanban, DollarSign, Wrench, AlertTriangle, TrendingUp } from 'lucide-react';

const API = 'https://crm.ashbournesystems.ca/api';

function StatCard({ icon: Icon, label, value, sub, color = '#B85A22' }) {
  return (
    <div style={{
      background: '#1a0f08', border: '1px solid #2a1a0e',
      padding: '1.5rem', borderTop: `3px solid ${color}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '0.65rem', color: '#8a6840', letterSpacing: '0.15em', marginBottom: '8px' }}>{label}</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#F5ECD7', lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: '0.72rem', color: '#8a6840', marginTop: '6px' }}>{sub}</div>}
        </div>
        <Icon size={24} color={color} opacity={0.6} />
      </div>
    </div>
  );
}

export default function Dashboard({ token }) {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/clients`, { headers }).then(r => setClients(r.data)).catch(() => {});
    axios.get(`${API}/projects`, { headers }).then(r => setProjects(r.data)).catch(() => {});
    axios.get(`${API}/invoices`, { headers }).then(r => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/maintenance/active`, { headers }).then(r => setMaintenance(r.data)).catch(() => {});
    axios.get(`${API}/invoices/overdue`, { headers }).then(r => setOverdue(r.data)).catch(() => {});
  }, []);

  const activeProjects = projects.filter(p => p.status === 'active').length;
  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
  const monthlyRecurring = maintenance.reduce((sum, m) => sum + parseFloat(m.monthly_fee || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#F5ECD7', marginBottom: '4px' }}>Dashboard</h1>
        <p style={{ color: '#8a6840', fontSize: '0.85rem' }}>Welcome back, Colton. Here's what's happening.</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <StatCard icon={Users} label="TOTAL CLIENTS" value={clients.length} sub="All time" />
        <StatCard icon={FolderKanban} label="ACTIVE PROJECTS" value={activeProjects} sub="In progress" color="#C8941A" />
        <StatCard icon={DollarSign} label="OUTSTANDING" value={`$${totalOutstanding.toFixed(0)}`} sub="Unpaid invoices" color="#8b2020" />
        <StatCard icon={Wrench} label="MAINTENANCE" value={maintenance.length} sub={`$${monthlyRecurring.toFixed(0)}/mo recurring`} color="#2d6a2d" />
      </div>

      {/* Overdue Invoices Alert */}
      {overdue.length > 0 && (
        <div style={{
          background: '#1a0808', border: '1px solid #8b2020', borderLeft: '4px solid #8b2020',
          padding: '1rem 1.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '1rem'
        }}>
          <AlertTriangle size={20} color="#8b2020" />
          <div>
            <div style={{ color: '#F5ECD7', fontSize: '0.85rem', fontWeight: 'bold' }}>
              {overdue.length} overdue invoice{overdue.length > 1 ? 's' : ''}
            </div>
            <div style={{ color: '#8a6840', fontSize: '0.75rem' }}>
              {overdue.map(i => `${i.business_name || i.client_name} — $${i.amount}`).join(', ')}
            </div>
          </div>
        </div>
      )}

      {/* Recent Clients */}
      <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: '#8a6840', letterSpacing: '0.15em' }}>RECENT CLIENTS</div>
          <TrendingUp size={16} color="#B85A22" />
        </div>
        {clients.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#8a6840', fontSize: '0.85rem' }}>
            No clients yet — go land that first one!
          </div>
        ) : (
          clients.slice(0, 5).map((c, i) => (
            <div key={c.id} style={{
              padding: '1rem 1.5rem', borderBottom: i < 4 ? '1px solid #2a1a0e' : 'none',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <div style={{ color: '#F5ECD7', fontSize: '0.85rem', fontWeight: 'bold' }}>{c.business_name || c.name}</div>
                <div style={{ color: '#8a6840', fontSize: '0.75rem' }}>{c.email}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {c.active_maintenance > 0 && (
                  <span style={{ fontSize: '0.6rem', background: 'rgba(45,106,45,0.2)', color: '#4a9a4a', padding: '2px 8px', border: '1px solid #2d6a2d' }}>
                    MAINTENANCE
                  </span>
                )}
                <span style={{ fontSize: '0.6rem', background: 'rgba(184,90,34,0.1)', color: '#B85A22', padding: '2px 8px', border: '1px solid #8C3F10' }}>
                  {c.project_count} PROJECT{c.project_count !== 1 ? 'S' : ''}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}